import { generatePath } from 'react-router-dom';

export const ROOT_CONTEST = '/contest';
export const ROOT_PROGRAM = '/program';
export const ROOT_FIND = '/find';
export const ROOT_MAIN_SECTION = '/main';

type PathParamValue = string | number;
type PathParams<TParam extends string> = Record<TParam, PathParamValue>;

const joinPaths = (...segments: string[]) => {
	const path = segments
		.map((segment) => segment.trim().replace(/^\/+|\/+$/g, ''))
		.filter(Boolean)
		.join('/');
	
	return `/${path}`;
};

const createPath = (root: string, ...segments: string[]) => joinPaths(root, ...segments);

const createPathBuilder = <TParam extends string>(path: string) => {
	return (params: PathParams<TParam>) => {
		const stringParams = Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
			acc[key] = String(value);
			return acc;
		}, {});
		
		return generatePath(path, stringParams);
	};
};

const contestPaths = {
	list: createPath(ROOT_CONTEST, 'list'),
	new: createPath(ROOT_CONTEST, 'new'),
	detail: createPath(ROOT_CONTEST, ':contestId'),
	edit: createPath(ROOT_CONTEST, ':contestId', 'edit')
} as const;

const contestParticipantPaths = {
	list: createPath(ROOT_CONTEST, 'participant', 'list'),
	detail: createPath(ROOT_CONTEST, 'participant', ':contestId')
} as const;

const programPaths = {
	list: createPath(ROOT_PROGRAM, 'list'),
	new: createPath(ROOT_PROGRAM, 'new'),
	detail: createPath(ROOT_PROGRAM, ':programId'),
	edit: createPath(ROOT_PROGRAM, ':programId', 'edit')
} as const;

const sessionPaths = {
	list: createPath(ROOT_PROGRAM, 'session', 'list'),
	new: createPath(ROOT_PROGRAM, 'session', 'new'),
	detail: createPath(ROOT_PROGRAM, ':programId', 'session', ':sessionId'),
	edit: createPath(ROOT_PROGRAM, ':programId', 'session', ':sessionId', 'edit')
} as const;

const contestDetailPath = createPathBuilder<'contestId'>(contestPaths.detail);
const contestEditPath = createPathBuilder<'contestId'>(contestPaths.edit);
const contestParticipantDetailPath = createPathBuilder<'contestId'>(contestParticipantPaths.detail);
const programDetailPath = createPathBuilder<'programId'>(programPaths.detail);
const programEditPath = createPathBuilder<'programId'>(programPaths.edit);
const sessionDetailPath = createPathBuilder<'programId' | 'sessionId'>(sessionPaths.detail);
const sessionEditPath = createPathBuilder<'programId' | 'sessionId'>(sessionPaths.edit);

export const paths = {
	home: '/',
	dashboard: '/dashboard',
	login: '/login',
	find: {
		email: createPath(ROOT_FIND, 'email'),
		password: createPath(ROOT_FIND, 'password')
	},
	// 메인 섹션
	mainSection: {
		// 최상단 배너
		topBanner: {
			list: createPath(ROOT_MAIN_SECTION, 'top-banner', 'list'),
			new: createPath(ROOT_MAIN_SECTION, 'top-banner', 'new')
		},
		// 명예의 전당
		hallOfFame: {
			list: createPath(ROOT_MAIN_SECTION, 'hall-of-fame')
		},
		// 추천 러닝 용품
		runningGear: {
			list: createPath(ROOT_MAIN_SECTION, 'running-gear')
		},
		// 공지사항
		notice: {
			list: createPath(ROOT_MAIN_SECTION, 'notice', 'list'),
			new: createPath(ROOT_MAIN_SECTION, 'notice', 'new')
		},
		// 자주 묻는 질문
		faq: {}
	},
	// 대회
	contest: {
		contest: {
			list: contestPaths.list,
			new: contestPaths.new,
			detail: contestPaths.detail,
			edit: contestPaths.edit,
			detailPath: (contestId: string) => contestDetailPath({ contestId }),
			editPath: (contestId: string) => contestEditPath({ contestId })
		},
		// 참가자 관리
		participant: {
			list: contestParticipantPaths.list,
			detail: contestParticipantPaths.detail,
			detailPath: (contestId: string) => contestParticipantDetailPath({ contestId })
		},
		// 이메일 발송
		email: {
			list: createPath(ROOT_CONTEST, 'email', 'list'),
			send: createPath(ROOT_CONTEST, 'email', 'send')
		},
		// 카카오 발송
		kakao: {
			list: createPath(ROOT_CONTEST, 'kakao', 'list')
		}
	},
	// 프로그램
	program: {
		program: {
			list: programPaths.list,
			new: programPaths.new,
			edit: programPaths.edit,
			detail: programPaths.detail,
			detailPath: (programId: string) => programDetailPath({ programId }),
			editPath: (programId: string) => programEditPath({ programId })
		},
		// 회차
		session: {
			list: sessionPaths.list,
			new: sessionPaths.new,
			detail: sessionPaths.detail,
			edit: sessionPaths.edit,
			detailPath: (programId: string, sessionId: number) => sessionDetailPath({ programId, sessionId }),
			editPath: (programId: string, sessionId: number) => sessionEditPath({ programId, sessionId })
		}
	}
} as const;
